import { useState, FormEvent } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function RequestFormPage() {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phone: '',
    email: '',
    city: '',
    apartmentType: '',
    message: '',
    kvkk: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ad alanı zorunludur';
    }

    if (!formData.surname.trim()) {
      newErrors.surname = 'Soyad alanı zorunludur';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon alanı zorunludur';
    } else if (!/^[0-9\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Geçerli bir telefon numarası giriniz';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'Şehir alanı zorunludur';
    }

    if (!formData.apartmentType) {
      newErrors.apartmentType = 'Daire tipi seçimi zorunludur';
    }

    if (formData.message.length > 500) {
      newErrors.message = 'Mesaj 500 karakterden fazla olamaz';
    }

    if (!formData.kvkk) {
      newErrors.kvkk = 'KVKK metnini onaylamanız gerekmektedir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('surname', formData.surname);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('city', formData.city);
      formBody.append('apartmentType', formData.apartmentType);
      formBody.append('message', formData.message || 'Mesaj girilmedi');
      formBody.append('kvkk', formData.kvkk ? 'Onaylandı' : 'Onaylanmadı');

      const response = await fetch('https://readdy.ai/api/form/d6rcpt4ddmmni7ck8rag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          surname: '',
          phone: '',
          email: '',
          city: '',
          apartmentType: '',
          message: '',
          kvkk: false
        });
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-64 md:h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/tk-ext-ref-1.png" 
            alt="Konut Talep Formu" 
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center text-white px-4 flex flex-col items-center">
          <div className="flex items-center gap-3 text-sm mb-4 font-medium">
            <a href="/" className="hover:text-red-400 transition-colors">Ana Sayfa</a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-200">Konut Talep Formu</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg tracking-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Konut Talep Formu
          </h1>
          <p className="text-lg mt-4 text-gray-200 drop-shadow max-w-xl">Hayalinizdeki eve kavuşmak için ilk adımı atın</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-200">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-600 text-white rounded-xl mb-4">
                <i className="ri-home-smile-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Neden KARE YAPI İNŞAAT?</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                  <span>Merkezi konum avantajı</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                  <span>Modern mimari tasarım</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                  <span>Zengin sosyal tesisler</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                  <span>Esnek ödeme planları</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                  <span>Güvenli yaşam alanı</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-phone-line text-teal-600"></i>
                Hemen Arayın
              </h4>
              <a href="tel:+905325003869" className="text-2xl font-bold text-teal-600 hover:text-teal-700 transition-colors">
                0 532 500 38 69
              </a>
              <p className="text-sm text-gray-600 mt-2">Pazartesi - Cumartesi<br />09:00 - 18:00</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-information-line text-teal-600"></i>
                Bilgi
              </h4>
              <p className="text-sm text-gray-600">
                Formu doldurduktan sonra uzman ekibimiz en kısa sürede sizinle iletişime geçecektir.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Bilgilerinizi Paylaşın</h2>
                <p className="text-gray-600">Lütfen aşağıdaki formu eksiksiz doldurunuz. Tüm alanlar zorunludur.</p>
              </div>

              <form id="housing-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                {/* Name & Surname */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Ad <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm`}
                      placeholder="Adınız"
                    />
                    {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="surname" className="block text-sm font-semibold text-gray-700 mb-2">
                      Soyad <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      value={formData.surname}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.surname ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm`}
                      placeholder="Soyadınız"
                    />
                    {errors.surname && <p className="text-red-600 text-xs mt-1">{errors.surname}</p>}
                  </div>
                </div>

                {/* Phone & Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefon <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm`}
                      placeholder="0 5XX XXX XX XX"
                    />
                    {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      E-posta <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm`}
                      placeholder="ornek@email.com"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* City & Apartment Type */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                      Şehir <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm`}
                      placeholder="İstanbul"
                    />
                    {errors.city && <p className="text-red-600 text-xs mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <label htmlFor="apartmentType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Daire Tipi <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="apartmentType"
                      name="apartmentType"
                      value={formData.apartmentType}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.apartmentType ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm cursor-pointer`}
                    >
                      <option value="">Daire tipi seçiniz</option>
                      <option value="2+1">2+1 Daire</option>
                      <option value="3+1">3+1 Daire</option>
                      <option value="3+1 Teraslı">3+1 Teraslı Daire</option>
                    </select>
                    {errors.apartmentType && <p className="text-red-600 text-xs mt-1">{errors.apartmentType}</p>}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Mesajınız (Opsiyonel)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className={`w-full px-4 py-3 border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none`}
                    placeholder="Özel talepleriniz veya sorularınız varsa buraya yazabilirsiniz..."
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    {errors.message && <p className="text-red-600 text-xs">{errors.message}</p>}
                    <p className="text-xs text-gray-500 ml-auto">{formData.message.length}/500 karakter</p>
                  </div>
                </div>

                {/* KVKK Consent */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="kvkk"
                      checked={formData.kvkk}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                      <strong className="text-red-600">*</strong> <strong>KVKK Aydınlatma Metni:</strong> Kişisel verilerimin, 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında işlenmesine ve tarafıma ürün/hizmet tanıtımı, kampanya, promosyon ve benzeri ticari elektronik ileti gönderilmesine açık rıza veriyorum.
                    </span>
                  </label>
                  {errors.kvkk && <p className="text-red-600 text-xs mt-2">{errors.kvkk}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 rounded-xl font-semibold text-base hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-xl"></i>
                      Formu Gönder
                    </>
                  )}
                </button>

                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl flex items-start gap-3">
                    <i className="ri-check-line text-2xl mt-0.5"></i>
                    <div>
                      <p className="font-semibold mb-1">Talebiniz Başarıyla Alındı!</p>
                      <p className="text-sm">Uzman ekibimiz en kısa sürede sizinle iletişime geçecektir.</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-xl flex items-start gap-3">
                    <i className="ri-error-warning-line text-2xl mt-0.5"></i>
                    <div>
                      <p className="font-semibold mb-1">Bir Hata Oluştu</p>
                      <p className="text-sm">Form gönderilirken bir sorun oluştu. Lütfen daha sonra tekrar deneyiniz veya bizi telefonla arayınız.</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}